import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon as Plus, FilePdfIcon as FilePdf, FileXlsIcon as FileXls, FileCsvIcon as FileCsv, CheckCircleIcon as CheckCircle, LockIcon as Lock, LockOpenIcon as LockOpen, ArrowClockwiseIcon as ArrowClockwise, WarningIcon as Warning, CurrencyEurIcon as CurrencyEur, TrashIcon as Trash } from "@phosphor-icons/react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MultiSelect } from "@/components/MultiSelect";
import { toast } from "sonner";

const PREFS_KEY = "ccs_filters_v1";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; } };

const err = (e) => formatApiErrorDetail(e?.response?.data?.detail);
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATO = {
  draft: { l: "Bozza", c: "bg-gray-100 text-gray-700" },
  incomplete: { l: "Incompleta", c: "bg-amber-100 text-amber-800" },
  verified: { l: "Verificata", c: "bg-blue-100 text-blue-800" },
  closed: { l: "Chiusa", c: "bg-green-100 text-green-800" },
  reopened: { l: "Riaperta", c: "bg-purple-100 text-purple-800" },
};

async function download(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = href; a.download = filename; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(href);
  } catch (e) { toast.error("Export non riuscito"); }
}

function DrillPanel({ title, head, rows, onClose, testid }) {
  return (
    <div className="border border-border rounded-sm overflow-x-auto" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 bg-secondary/40">
        <span className="text-sm font-medium">{title}</span>
        <Button size="sm" variant="ghost" className="h-6" onClick={onClose} data-testid={`${testid}-close`}>Chiudi elenco</Button>
      </div>
      <Table>
        <TableHeader><TableRow>{head.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 ? <TableRow><TableCell colSpan={head.length} className="text-center py-5 text-muted-foreground">Nessun elemento</TableCell></TableRow>
            : rows.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j} className="text-xs">{c}</TableCell>)}</TableRow>)}
        </TableBody>
      </Table>
    </div>
  );
}

export function CopyCostStatements({ contractId = null, deviceId = null }) {
  const global = !contractId && !deviceId;
  const P = global ? loadPrefs() : {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState({});
  const [revenue, setRevenue] = useState({ imponibile_stimato: 0 });
  const [detail, setDetail] = useState(null);
  const [delRow, setDelRow] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ mode: "previous", period_start: "", period_end: "" });
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [drill, setDrill] = useState(null);            // 'missing' | 'retro' | null
  const [retroRows, setRetroRows] = useState([]);
  // filtri (multiselezione) persistiti in localStorage
  const [fCli, setFCli] = useState(P.fCli || []);
  const [fCon, setFCon] = useState(P.fCon || []);
  const [fStati, setFStati] = useState(P.fStati || []);
  const [fFrom, setFFrom] = useState(P.fFrom || "");
  const [fTo, setFTo] = useState(P.fTo || "");
  const [quick, setQuick] = useState(P.quick || { inc: false, anom: false, rett: false });

  useEffect(() => {
    if (!global) return;
    localStorage.setItem(PREFS_KEY, JSON.stringify({ fCli, fCon, fStati, fFrom, fTo, quick }));
  }, [global, fCli, fCon, fStati, fFrom, fTo, quick]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (contractId) params.contract_id = contractId;
      if (deviceId) params.device_id = deviceId;
      const { data } = await api.get("/copy-cost-statements", { params });
      setRows(data || []);
      if (global) {
        api.get("/copy-cost-statements/alerts").then(({ data }) => setAlerts(data || {})).catch(() => {});
        api.get("/copy-cost-statements/estimated-revenue").then(({ data }) => setRevenue(data || {})).catch(() => {});
        api.get("/clients").then(({ data }) => setClients((data?.items || data || []).map((x) => ({ value: x.id, label: x.ragione_sociale })))).catch(() => {});
        api.get("/copy-cost-contracts").then(({ data }) => setContracts((data || []).map((x) => ({ value: x.id, label: `${x.numero} · ${x.cliente_nome || ""}` })))).catch(() => {});
      }
    } finally { setLoading(false); }
  }, [contractId, deviceId, global]);
  useEffect(() => { load(); }, [load]);

  const hasAdj = (s) => (s.total_adjustments || 0) !== 0 || (s.devices || []).some((d) => d.adj_copie_mono || d.adj_copie_colore || d.adj_importo_dispositivo);
  const hasAnom = (s) => (s.devices || []).some((d) => (d.incomplete_reasons || []).some((r) => r.includes("anomal")));
  const filtered = useMemo(() => rows.filter((s) => {
    if (fCli.length && !fCli.includes(s.cliente_id)) return false;
    if (fCon.length && !fCon.includes(s.contract_id)) return false;
    if (fStati.length && !fStati.includes(s.status)) return false;
    if (fFrom && (s.period_end || "") < fFrom) return false;
    if (fTo && (s.period_start || "") > fTo) return false;
    if (quick.inc && s.status !== "incomplete") return false;
    if (quick.anom && !hasAnom(s)) return false;
    if (quick.rett && !hasAdj(s)) return false;
    return true;
  }), [rows, fCli, fCon, fStati, fFrom, fTo, quick]);

  const openDrillRetro = () => { setDrill("retro"); api.get("/copy-cost-statements/retro-alerts").then(({ data }) => setRetroRows(data || [])).catch(() => setRetroRows([])); };

  const doGenerate = async () => {
    try {
      const body = { contract_id: contractId, mode: genForm.mode };
      if (genForm.mode === "custom") { body.period_start = genForm.period_start; body.period_end = genForm.period_end; }
      const { data } = await api.post("/copy-cost-statements/generate", body);
      toast.success(data.message || "Rendicontazione generata");
      setGenOpen(false); await load(); setDetail(data);
    } catch (e) { toast.error(err(e)); }
  };

  const kpis = useMemo(() => ([
    { k: "to_verify", label: "Da verificare", val: alerts.to_verify ?? 0, icon: CheckCircle, cls: "text-blue-700" },
    { k: "incomplete", label: "Incomplete", val: alerts.incomplete ?? 0, icon: Warning, cls: "text-amber-700" },
    { k: "closed", label: "Chiuse", val: alerts.closed ?? 0, icon: Lock, cls: "text-green-700" },
    { k: "__missing", label: "Periodi non generati", val: alerts.periods_missing ?? 0, icon: ArrowClockwise, cls: "text-slate-700" },
    { k: "__retro", label: "Retroattive aperte", val: alerts.retroactive_open ?? 0, icon: Warning, cls: "text-red-700" },
  ]), [alerts]);

  const expQ = useMemo(() => {
    const p = new URLSearchParams();
    if (fStati.length) p.set("stati", fStati.join(","));
    if (fCli.length) p.set("clienti", fCli.join(","));
    if (fCon.length) p.set("contratti", fCon.join(","));
    if (fFrom) p.set("pfrom", fFrom);
    if (fTo) p.set("pto", fTo);
    if (quick.inc) p.set("inc", "1");
    if (quick.anom) p.set("anom", "1");
    if (quick.rett) p.set("rett", "1");
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [fStati, fCli, fCon, fFrom, fTo, quick]);
  const resetFilters = () => { setFCli([]); setFCon([]); setFStati([]); setFFrom(""); setFTo(""); setQuick({ inc: false, anom: false, rett: false }); };
  const activeFilters = fStati.length || fCli.length || fCon.length || fFrom || fTo || quick.inc || quick.anom || quick.rett;
  return (
    <div data-testid="copy-cost-statements">
      {global && (
        <>
          <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 mb-3 flex items-center gap-3" data-testid="ccs-estimated-revenue">
            <CurrencyEur size={22} weight="fill" className="text-emerald-700" />
            <div>
              <div className="text-xs text-emerald-700 font-medium">Ricavo costo copia stimato — non ancora rendicontato (imponibile)</div>
              <div className="text-2xl font-semibold text-emerald-800">{eur(revenue.imponibile_stimato)}</div>
            </div>
            <span className="ml-auto text-[11px] text-emerald-700 bg-emerald-100 rounded-sm px-2 py-1">Stimato / non ancora rendicontato</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {kpis.map((k) => (
              <button key={k.k} data-testid={`ccs-kpi-${k.k}`}
                onClick={() => {
                  if (k.k === "to_verify") { setFStati(["draft", "reopened"]); setDrill(null); }
                  else if (["incomplete", "closed"].includes(k.k)) { setFStati([k.k]); setDrill(null); }
                  else if (k.k === "__missing") setDrill("missing");
                  else if (k.k === "__retro") openDrillRetro();
                }}
                className={`rounded-sm border border-border bg-white px-3 py-2 text-left hover:bg-secondary/40 transition-colors ${(drill === "missing" && k.k === "__missing") || (drill === "retro" && k.k === "__retro") ? "ring-2 ring-primary" : ""}`}>
                <div className={`flex items-center gap-1.5 text-xs ${k.cls}`}><k.icon size={14} weight="fill" /> {k.label}</div>
                <div className="text-xl font-semibold">{k.val}</div>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <MultiSelect options={[{ value: "draft", label: "Bozza" }, { value: "incomplete", label: "Incompleta" }, { value: "verified", label: "Verificata" }, { value: "closed", label: "Chiusa" }, { value: "reopened", label: "Riaperta" }]}
            value={fStati} onChange={setFStati} placeholder="Stato" width="w-36" testid="ccs-filter-stato" />
          {global && <>
            <MultiSelect options={clients} value={fCli} onChange={setFCli} placeholder="Cliente" width="w-44" searchable testid="ccs-filter-cliente" />
            <MultiSelect options={contracts} value={fCon} onChange={setFCon} placeholder="Contratto" width="w-44" searchable testid="ccs-filter-contratto" />
          </>}
          <Input type="date" className="rounded-sm h-9 w-36" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="ccs-filter-from" title="Periodo da" />
          <Input type="date" className="rounded-sm h-9 w-36" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="ccs-filter-to" title="Periodo a" />
          <Button variant={quick.inc ? "default" : "outline"} size="sm" className="rounded-sm h-9" onClick={() => setQuick((q) => ({ ...q, inc: !q.inc }))} data-testid="ccs-quick-incomplete">Incomplete</Button>
          <Button variant={quick.anom ? "default" : "outline"} size="sm" className="rounded-sm h-9" onClick={() => setQuick((q) => ({ ...q, anom: !q.anom }))} data-testid="ccs-quick-anomalie">Anomalie</Button>
          <Button variant={quick.rett ? "default" : "outline"} size="sm" className="rounded-sm h-9" onClick={() => setQuick((q) => ({ ...q, rett: !q.rett }))} data-testid="ccs-quick-rettifiche">Rettifiche</Button>
          {global && activeFilters ? <Button variant="ghost" size="sm" className="rounded-sm h-9 text-muted-foreground" onClick={resetFilters} data-testid="ccs-filters-reset">Azzera filtri</Button> : null}
        </div>
        <div className="flex items-center gap-2">
          {global && (
            <>
              <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="ccs-export-xlsx"
                onClick={() => download(`/copy-cost-statements/export/xlsx${expQ}`, "Rendicontazioni.xlsx")}><FileXls size={15} /> Excel</Button>
              <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="ccs-export-csv"
                onClick={() => download(`/copy-cost-statements/export/csv${expQ}`, "Rendicontazioni.zip")}><FileCsv size={15} /> CSV</Button>
              <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="ccs-export-pdf"
                onClick={() => download(`/copy-cost-statements/export/pdf${expQ}`, "Rendicontazioni.pdf")}><FilePdf size={15} /> PDF</Button>
            </>
          )}
          {contractId && (
            <Button size="sm" className="rounded-sm gap-1.5" data-testid="ccs-generate-btn" onClick={() => setGenOpen(true)}>
              <Plus size={15} /> Genera rendicontazione
            </Button>
          )}
        </div>
      </div>

      {drill === "missing" ? (
        <DrillPanel title="Periodi scaduti / non generati" onClose={() => setDrill(null)}
          rows={(alerts.missing_list || []).map((m) => [`${m.numero}`, `${m.period_start} → ${m.period_end}`])}
          head={["Contratto", "Periodo mancante"]} testid="ccs-drill-missing" />
      ) : drill === "retro" ? (
        <DrillPanel title="Retroattive aperte su periodi chiusi" onClose={() => setDrill(null)}
          rows={retroRows.map((r) => [r.reading_at?.slice(0, 10), `${r.period_start} → ${r.period_end}`, String(r.reading_value)])}
          head={["Lettura del", "Periodo chiuso", "Valore"]} testid="ccs-drill-retro" />
      ) : (
      <div className="border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Contratto</TableHead><TableHead>Periodo</TableHead><TableHead>Stato</TableHead>
            <TableHead>Disp.</TableHead><TableHead>Totale</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Caricamento…</TableCell></TableRow>
              : filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Nessuna rendicontazione</TableCell></TableRow>
                : filtered.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-secondary/30" onClick={() => setDetail(s)} data-testid={`ccs-row-${s.id}`}>
                    <TableCell className="text-xs font-medium">{s.numero}<div className="text-[10px] text-muted-foreground">{s.titolo}</div></TableCell>
                    <TableCell className="text-xs">{s.period_start} → {s.period_end}</TableCell>
                    <TableCell><Badge variant="outline" className={`rounded-sm text-[10px] ${STATO[s.status]?.c || ""}`}>{STATO[s.status]?.l || s.status}</Badge></TableCell>
                    <TableCell className="text-xs">{(s.devices || []).length}</TableCell>
                    <TableCell className="text-xs font-medium">{eur(s.total_amount)}</TableCell>
                    <TableCell className="text-right text-[10px] text-muted-foreground">
                      <div className="flex items-center justify-end gap-1">
                        <span>v{s.export_version}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" title="Elimina rendicontazione" data-testid={`ccs-delete-${s.id}`}
                          onClick={(e) => { e.stopPropagation(); setDelRow(s); }}><Trash size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>
      )}

      {detail && <StatementDetail statement={detail} onClose={() => { setDetail(null); load(); }} />}

      <AlertDialog open={!!delRow} onOpenChange={(v) => !v && setDelRow(null)}>
        <AlertDialogContent data-testid="ccs-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la rendicontazione?</AlertDialogTitle>
            <AlertDialogDescription>
              {delRow ? `${delRow.numero} · ${delRow.period_start} → ${delRow.period_end}. ` : ""}
              L'operazione è definitiva e rimuove anche le relative rettifiche. {delRow?.status === "closed" ? "Una rendicontazione chiusa va prima riaperta." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ccs-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="ccs-delete-confirm"
              onClick={async () => {
                try { await api.delete(`/copy-cost-statements/${delRow.id}`); toast.success("Rendicontazione eliminata"); setDelRow(null); load(); }
                catch (e) { toast.error(err(e)); }
              }}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="ccs-generate-dialog">
          <DialogHeader><DialogTitle>Genera rendicontazione</DialogTitle>
            <DialogDescription>Periodo derivato dalla periodicità del contratto. Idempotente: se esiste già viene aperta.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Periodo</Label>
              <Select value={genForm.mode} onValueChange={(v) => setGenForm((f) => ({ ...f, mode: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="ccs-gen-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="previous">Periodo precedente</SelectItem>
                  <SelectItem value="current">Periodo corrente</SelectItem>
                  <SelectItem value="custom">Intervallo personalizzato</SelectItem>
                </SelectContent>
              </Select></div>
            {genForm.mode === "custom" && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Da</Label><Input type="date" className="rounded-sm" value={genForm.period_start} onChange={(e) => setGenForm((f) => ({ ...f, period_start: e.target.value }))} data-testid="ccs-gen-start" /></div>
                <div><Label className="text-xs">A</Label><Input type="date" className="rounded-sm" value={genForm.period_end} onChange={(e) => setGenForm((f) => ({ ...f, period_end: e.target.value }))} data-testid="ccs-gen-end" /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setGenOpen(false)}>Annulla</Button>
            <Button className="rounded-sm" onClick={doGenerate} data-testid="ccs-gen-confirm">Genera</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatementDetail({ statement, onClose }) {
  const [s, setS] = useState(statement);
  const mutable = ["draft", "incomplete", "reopened"].includes(s.status);
  const refresh = async () => { const { data } = await api.get(`/copy-cost-statements/${s.id}`); setS(data); };
  const act = async (path, body) => {
    try { const { data } = await api.post(`/copy-cost-statements/${s.id}/${path}`, body || {}); setS(data); toast.success("Aggiornata"); }
    catch (e) { toast.error(err(e)); }
  };
  const reopen = async () => {
    const reason = window.prompt("Causale di riapertura (obbligatoria):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("Causale obbligatoria"); return; }
    await act("reopen", { reason });
  };
  const fnBase = `Rendicontazione_${s.numero}_${(s.period_start || "").slice(0, 7)}`;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[860px] max-h-[90vh] overflow-y-auto" data-testid="ccs-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{s.numero} · {s.period_start} → {s.period_end}
            <Badge variant="outline" className={`rounded-sm text-[10px] ${STATO[s.status]?.c || ""}`} data-testid="ccs-detail-status">{STATO[s.status]?.l}</Badge>
          </DialogTitle>
          <DialogDescription>Valori imponibili — IVA esclusa. {s.reopen_reason ? `Riaperta: ${s.reopen_reason}` : ""}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 mb-2">
          {s.status !== "closed" && !["incomplete"].includes(s.status) && s.status !== "verified" && (
            <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => act("verify")} data-testid="ccs-verify-btn"><CheckCircle size={14} /> Verifica</Button>
          )}
          {s.status === "verified" && <>
            <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => act("unverify")} data-testid="ccs-unverify-btn">Torna in bozza</Button>
            <Button size="sm" className="rounded-sm gap-1.5" onClick={() => act("close")} data-testid="ccs-close-btn"><Lock size={14} /> Chiudi</Button>
          </>}
          {s.status === "closed" && <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={reopen} data-testid="ccs-reopen-btn"><LockOpen size={14} /> Riapri</Button>}
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => download(`/copy-cost-statements/${s.id}/export/pdf`, `${fnBase}.pdf`)} data-testid="ccs-detail-pdf"><FilePdf size={14} /> PDF</Button>
            <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => download(`/copy-cost-statements/${s.id}/export/xlsx`, `${fnBase}.xlsx`)} data-testid="ccs-detail-xlsx"><FileXls size={14} /> Excel</Button>
            <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => download(`/copy-cost-statements/${s.id}/export/csv`, `${fnBase}.csv`)} data-testid="ccs-detail-csv"><FileCsv size={14} /> CSV</Button>
          </div>
        </div>

        <div className="border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Dispositivo</TableHead><TableHead>Iniz./Fin. mono</TableHead><TableHead>Mono</TableHead>
              <TableHead>Colore</TableHead><TableHead>Canone</TableHead><TableHead>Minimo</TableHead><TableHead>Totale</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(s.devices || []).map((d) => {
                const rm = d.reading_final?.mono, im = d.reading_initial?.mono;
                return (
                  <TableRow key={d.device_link_id} data-testid={`ccs-dev-${d.device_link_id}`}>
                    <TableCell className="text-xs font-medium">{d.device_marca} {d.device_modello}
                      <div className="text-[10px] text-muted-foreground">{d.device_serial}</div>
                      {d.incomplete && <Badge variant="outline" className="rounded-sm text-[9px] bg-amber-50 text-amber-700 border-amber-200 mt-0.5">{d.incomplete_reasons?.[0] || "incompleta"}</Badge>}
                    </TableCell>
                    <TableCell className="text-[11px]">{im ? im.value : "—"} → {rm ? rm.value : "—"}
                      {rm && !rm.is_exact && <div className="text-[9px] text-amber-600">ultima del {(rm.at || "").slice(0, 10)}</div>}
                    </TableCell>
                    <TableCell className="text-xs">{d.consumo_mono_finale}{d.adj_copie_mono ? <span className="text-blue-600"> ({d.adj_copie_mono > 0 ? "+" : ""}{d.adj_copie_mono})</span> : ""}</TableCell>
                    <TableCell className="text-xs">{d.consumo_colore_finale}</TableCell>
                    <TableCell className="text-xs">{eur(d.economics?.canone_dispositivo)}</TableCell>
                    <TableCell className="text-xs">{d.economics?.minimo_applicato ? <span className="text-amber-700">{eur(d.economics?.minimo_fatturabile)}</span> : eur(d.economics?.minimo_fatturabile)}</TableCell>
                    <TableCell className="text-xs font-medium">{eur(d.totale_dispositivo)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-end mt-3">
          <div className="text-sm space-y-1 text-right">
            <div>Canone generale: <b>{eur(s.general_fee)}</b></div>
            <div>Subtotale dispositivi: <b>{eur(s.subtotal_devices)}</b></div>
            <div>Rettifiche (importi): <b>{eur(s.total_adjustments)}</b></div>
            <div className="text-base">Totale (imponibile): <b data-testid="ccs-detail-total">{eur(s.total_amount)}</b></div>
          </div>
        </div>

        {mutable && <StmtAdjustments statement={s} onChange={refresh} />}
      </DialogContent>
    </Dialog>
  );
}

function StmtAdjustments({ statement, onChange }) {
  const [adjs, setAdjs] = useState([]);
  const [form, setForm] = useState({ tipo: "add_importo", importo: 0, quantita: 0, causale: "", device_link_id: "" });
  const load = useCallback(() => api.get(`/copy-cost-statements/${statement.id}/adjustments`).then(({ data }) => setAdjs(data || [])).catch(() => {}), [statement.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.causale.trim()) { toast.error("Causale obbligatoria"); return; }
    try {
      await api.post(`/copy-cost-statements/${statement.id}/adjustments`, { ...form, device_link_id: form.device_link_id || null });
      setForm({ tipo: "add_importo", importo: 0, quantita: 0, causale: "", device_link_id: "" });
      load(); onChange();
    } catch (e) { toast.error(err(e)); }
  };
  const del = async (id) => { await api.delete(`/copy-cost-statements/${statement.id}/adjustments/${id}`); load(); onChange(); };
  const isCopie = form.tipo.includes("copie");
  return (
    <div className="mt-4 border-t border-border pt-3" data-testid="ccs-adjustments">
      <div className="text-sm font-medium mb-2">Rettifiche</div>
      {adjs.filter((a) => a.stato === "attiva").map((a) => (
        <div key={a.id} className="flex items-center gap-2 text-xs py-1">
          <Badge variant="outline" className="rounded-sm text-[10px]">{a.tipo}</Badge>
          <span>{a.quantita ? `${a.quantita} copie` : eur(a.importo)}</span>
          <span className="text-muted-foreground flex-1 truncate">{a.causale}</span>
          <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => del(a.id)} data-testid={`ccs-adj-del-${a.id}`}>Annulla</Button>
        </div>
      ))}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 items-end">
        <div><Label className="text-[10px]">Tipo</Label>
          <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
            <SelectTrigger className="rounded-sm h-8" data-testid="ccs-adj-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["add_importo", "sub_importo", "rettifica_canone", "add_copie_mono", "sub_copie_mono", "add_copie_colore", "sub_copie_colore", "altro"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select></div>
        {isCopie
          ? <div><Label className="text-[10px]">Copie</Label><Input type="number" className="rounded-sm h-8" value={form.quantita} onChange={(e) => setForm((f) => ({ ...f, quantita: parseFloat(e.target.value) || 0 }))} data-testid="ccs-adj-qta" /></div>
          : <div><Label className="text-[10px]">Importo €</Label><Input type="number" step="0.01" className="rounded-sm h-8" value={form.importo} onChange={(e) => setForm((f) => ({ ...f, importo: parseFloat(e.target.value) || 0 }))} data-testid="ccs-adj-importo" /></div>}
        {form.tipo.includes("copie") && (
          <div><Label className="text-[10px]">Dispositivo</Label>
            <Select value={form.device_link_id} onValueChange={(v) => setForm((f) => ({ ...f, device_link_id: v }))}>
              <SelectTrigger className="rounded-sm h-8" data-testid="ccs-adj-device"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{(statement.devices || []).map((d) => <SelectItem key={d.device_link_id} value={d.device_link_id}>{d.device_serial}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
        <div className={form.tipo.includes("copie") ? "md:col-span-4" : ""}><Label className="text-[10px]">Causale *</Label><Input className="rounded-sm h-8" value={form.causale} onChange={(e) => setForm((f) => ({ ...f, causale: e.target.value }))} data-testid="ccs-adj-causale" /></div>
      </div>
      <Button size="sm" className="rounded-sm mt-2" onClick={add} data-testid="ccs-adj-add">Aggiungi rettifica</Button>
    </div>
  );
}
